import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, FolderOpen, MessageSquare, FileText, Wallet } from 'lucide-react';
import { useAuth, api } from '../../context/AuthContext';

export const PORTAL_PFAD = /^\/(dashboard|portal|meine-akten|dokumente|meine-raten|profil|downloads|rueckruf|sepa-lastschrift|ratenzahlung)/;
export const portalAktualisieren = () => window.dispatchEvent(new Event('portal-aktualisiert'));

const TABS = [
  { id: 'start', to: '/dashboard', icon: Home, key: 'portal.tab.start', de: 'Start', match: /^\/(dashboard|portal\/?$)/ },
  { id: 'akten', to: '/portal/akten', icon: FolderOpen, key: 'portal.tab.akten', de: 'Akten', match: /^\/portal\/akten/, zahl: 'neu' },
  { id: 'chat', to: '/meine-akten', icon: MessageSquare, key: 'portal.tab.chat', de: 'Chat', match: /^\/meine-akten/, zahl: 'chat' },
  { id: 'dokumente', to: '/dokumente', icon: FileText, key: 'portal.tab.dokumente', de: 'Dokumente', match: /^\/dokumente/ },
  { id: 'zahlungen', to: '/portal/zahlungen', icon: Wallet, key: 'portal.tab.zahlungen', de: 'Zahlungen', match: /^\/(portal\/zahlungen|meine-raten)/ },
];

const useZahlen = () => {
  const [z, setZ] = useState({ neu: 0, chat: 0 });
  useEffect(() => {
    let alive = true;
    const load = () => {
      if (document.hidden) return;
      Promise.all([api.get('/portal/uebersicht').catch(() => null), api.get('/cases').catch(() => null)]).then(([u, c]) => {
        if (alive) setZ({ neu: u?.data?.neu || 0, chat: (c?.data?.items || []).reduce((s, i) => s + (i.unread || 0), 0) });
      });
    };
    load();
    const t = setInterval(load, 60000);
    window.addEventListener('portal-aktualisiert', load);
    return () => { alive = false; clearInterval(t); window.removeEventListener('portal-aktualisiert', load); };
  }, []);
  return z;
};

export const usePortalDockSichtbar = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  return user?.role === 'user' && PORTAL_PFAD.test(pathname);
};

const Dock = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const zahlen = useZahlen();
  return (
    <nav aria-label={t('portal.nav', 'Mandantenportal')} data-testid="portal-dock"
      className="fixed bottom-3 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-1.5rem)] max-w-md rounded-[20px] bg-[#0B1724]/85 backdrop-blur-[16px] border border-[#b8893d]/40 shadow-[0_18px_40px_-18px_rgba(11,23,36,0.8)] pb-[env(safe-area-inset-bottom)]">
      <ul className="grid grid-cols-5">
        {TABS.map((tab) => {
          const aktiv = tab.match.test(pathname);
          const n = tab.zahl ? zahlen[tab.zahl] : 0;
          return (
            <li key={tab.id}>
              <Link to={tab.to} data-testid={`portal-dock-${tab.id}`} aria-current={aktiv ? 'page' : undefined}
                className="relative flex flex-col items-center gap-0.5 py-2.5 text-[10px] tracking-wide focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e0b56a]/70 rounded-[16px]">
                <span className={`relative inline-flex items-center justify-center w-11 h-7 rounded-full transition-colors duration-200 ${aktiv ? 'bg-[#e0b56a]/20' : ''}`}>
                  <tab.icon className={`w-[18px] h-[18px] transition-colors ${aktiv ? 'text-[#e0b56a] drop-shadow-[0_0_6px_#e0b56a]' : 'text-white/70'}`} strokeWidth={1.75} />
                  {n > 0 && <span className="absolute -top-1 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#b8893d] text-[#0B1724] text-[9px] font-bold inline-flex items-center justify-center" data-testid={`portal-dock-zahl-${tab.id}`}>{n}</span>}
                </span>
                <span className={aktiv ? 'text-white' : 'text-white/60'}>{t(tab.key, tab.de)}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export const PortalDock = () => (usePortalDockSichtbar() ? <Dock /> : null);

export default PortalDock;
